import type { QualityCheckDto } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { ApiError, api, type PendingLot } from '../lib/api';
import { formatDateTime, formatQuantity, humanise } from '../lib/format';
import { useDeepLink } from '../lib/useDeepLink';

/**
 * Quality control — the gate on quarantined material.
 *
 * Quarantined stock is real, valued and visible; it simply cannot be drawn into
 * production until someone decides it is fit. That decision is what this page
 * records, and it is always attributed.
 */
export function QualityPage() {
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<PendingLot | null>(null);
  const [result, setResult] = useState('');
  const [requestedLotId, setRequestedLotId] = useState<string | null>(null);

  /** The dashboard can link straight to a lot's decision. */
  useDeepLink('lot', (value) => setRequestedLotId(value));

  const pending = useQuery({
    queryKey: ['quality', 'pending'],
    queryFn: api.quality.pending,
  });

  const history = useQuery({
    queryKey: ['quality', 'history'],
    queryFn: () => api.quality.list({ limit: 25 }),
  });

  const decide = useMutation({
    mutationFn: (variables: { lot: PendingLot; outcome: 'passed' | 'failed' }) =>
      api.quality.decide({
        lotId: variables.lot.lotId,
        outcome: variables.outcome,
        ...(result.trim() ? { result: result.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quality'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setDeciding(null);
      setResult('');
    },
  });

  const onHold = pending.data?.items ?? [];

  /**
   * A deep link names a lot, but the queue arrives asynchronously — so open the
   * decision once the data is here, and only if the lot is still on hold.
   */
  useEffect(() => {
    if (!requestedLotId || !pending.isSuccess) return;
    const lot = pending.data.items.find((candidate) => candidate.lotId === requestedLotId);
    if (lot) {
      setDeciding(lot);
      setResult('');
      decide.reset();
    }
    setRequestedLotId(null);
  }, [requestedLotId, pending.isSuccess, pending.data, decide]);

  const pendingColumns: DataColumn<PendingLot>[] = [
    {
      key: 'lot',
      header: 'Lot',
      cell: (lot) => <span className="font-mono text-xs">{lot.lotCode}</span>,
    },
    {
      key: 'material',
      header: 'Material',
      primary: true,
      cell: (lot) => (
        <>
          <span className="font-mono text-xs">{lot.itemCode}</span>
          <br />
          <span className="text-ink-body">{lot.itemName}</span>
        </>
      ),
    },
    {
      key: 'why',
      header: (
        <span className="inline-flex items-center gap-1.5">
          Why it is here
          <Tooltip
            subject="Why a lot is on hold"
            text="“Purchased” material arrived somewhere that requires QC release. “Recovered” material is on hold because the item says recoveries must be checked before use, or because the person recording it chose that."
          />
        </span>
      ),
      cardLabel: 'Why it is here',
      cell: (lot) => (
        <>
          <Badge tone={lot.origin === 'recovered' ? 'accent' : 'muted'}>
            {humanise(lot.origin)}
          </Badge>
          {lot.supplierName ? (
            <span className="ml-2 text-xs text-ink-muted">{lot.supplierName}</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'onHand',
      header: 'On hand',
      align: 'right',
      cell: (lot) => (
        <span className="whitespace-nowrap font-semibold text-ink">
          {formatQuantity(lot.onHand, lot.baseUom)}
        </span>
      ),
    },
    {
      key: 'heldSince',
      header: 'Held since',
      cell: (lot) => (
        <span className="whitespace-nowrap text-xs">{formatDateTime(lot.onHoldSince)}</span>
      ),
    },
  ];

  const historyColumns: DataColumn<QualityCheckDto>[] = [
    {
      key: 'when',
      header: 'When',
      cell: (check) => (
        <span className="whitespace-nowrap text-xs">{formatDateTime(check.checkedAt)}</span>
      ),
    },
    {
      key: 'lot',
      header: 'Lot',
      cell: (check) => <span className="font-mono text-xs">{check.lotCode}</span>,
    },
    {
      key: 'material',
      header: 'Material',
      primary: true,
      cell: (check) => (
        <>
          <span className="font-mono text-xs">{check.itemCode}</span>
          <br />
          <span className="text-ink-body">{check.itemName}</span>
        </>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      align: 'right',
      cell: (check) => (
        <>
          <Badge tone={check.status === 'passed' ? 'green' : 'muted'}>{check.status}</Badge>
          <span className="ml-2 text-xs text-ink-muted">
            lot is {humanise(check.lotStatus)}
          </span>
        </>
      ),
    },
    { key: 'result', header: 'Result', cell: (check) => (
      <span className="text-xs">{check.result ?? '—'}</span>
    ) },
    { key: 'by', header: 'Decided by', cell: (check) => (
      <span className="text-xs">{check.checkedByName ?? '—'}</span>
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Quality"
        title="Waiting for release"
        description="Material on hold is counted, valued and visible — it just cannot be drawn into production. A passed check makes it usable; a failed check rejects it, which is a one-way door that has to be reopened deliberately."
      />

      <Card className="mb-6">
        <CardBody>
          <div className="mb-4 flex items-center gap-3">
            <h2 className="text-base font-bold text-ink">
              {onHold.length} lot{onHold.length === 1 ? '' : 's'} on hold
            </h2>
            {pending.isSuccess && onHold.length > 0 ? (
              <span className="text-sm text-ink-muted">oldest first</span>
            ) : null}
          </div>

          {pending.isPending ? <Spinner /> : null}
          <ErrorNote>
            {pending.isError ? 'Could not load the hold queue. Is the API running?' : ''}
          </ErrorNote>

          {pending.isSuccess ? (
            <DataTable
              rows={onHold}
              columns={pendingColumns}
              rowKey={(lot) => lot.lotId}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actionsHeader={
                <span className="inline-flex items-center gap-1.5">
                  Decision
                  <Tooltip
                    subject="Releasing and rejecting"
                    text="Releasing makes the whole lot usable in production immediately. Rejecting is the opposite and is final in practice — it has to be reopened deliberately before it can be tested again."
                  />
                </span>
              }
              actions={(lot) => (
                <Button
                  onClick={() => {
                    setDeciding(lot);
                    setResult('');
                    decide.reset();
                  }}
                >
                  Decide
                </Button>
              )}
              empty="Nothing is on hold. Material lands here when it is received or recovered into a location flagged for QC release."
            />
          ) : null}

          {deciding ? (
            <div className="mt-4 rounded-md border-l-4 border-l-accent bg-accent-soft p-4">
              <h3 className="text-sm font-bold uppercase tracking-wide text-accent">
                {deciding.lotCode} — {deciding.itemCode}
              </h3>
              <p className="mt-1 text-sm text-ink-body">
                {formatQuantity(deciding.onHand, deciding.baseUom)} · {humanise(deciding.origin)} ·
                held since {formatDateTime(deciding.onHoldSince)}
              </p>

              <div className="mt-3 max-w-xl">
                <Field label="Result" hint="What the check found. Recorded with your name.">
                  <input
                    name="qualityResult"
                    className={inputClass}
                    value={result}
                    onChange={(event) => setResult(event.target.value)}
                    placeholder="e.g. GC 99.6%, water 0.2%"
                  />
                </Field>
              </div>

              {decide.error ? (
                <div className="mt-3">
                  <ErrorNote>
                    {decide.error instanceof ApiError
                      ? decide.error.message
                      : 'Could not record the decision.'}
                  </ErrorNote>
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ lot: deciding, outcome: 'passed' })}
                >
                  {decide.isPending ? 'Saving…' : 'Release — make it usable'}
                </Button>
                <Button
                  tone="danger"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ lot: deciding, outcome: 'failed' })}
                >
                  Reject
                </Button>
                <Button tone="ghost" onClick={() => setDeciding(null)}>
                  Cancel
                </Button>
              </div>

              <div className="mt-3">
                <InfoNote>
                  Releasing passes the lot as usable everywhere. Rejecting marks it rejected, and a
                  later pass will not undo that — a rejected lot has to be reopened for re-testing
                  first.
                </InfoNote>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="text-base font-bold text-ink">Recent decisions</h2>
          <p className="mt-1 text-sm font-light text-ink-body">
            Every decision is attributed and kept, because an audit asks who released what and on
            what basis.
          </p>

          <div className="mt-4">
            {history.isPending ? <Spinner /> : null}
            {history.isSuccess ? (
              <DataTable
                rows={history.data.items}
                columns={historyColumns}
                rowKey={(check) => check.id}
                empty="No decisions recorded yet."
              />
            ) : null}
          </div>
        </CardBody>
      </Card>
    </>
  );
}
